import { extractCaseIds, parseCaseTokens } from "@/api/club/caseTokens";
import type { ClubFeedItem } from "@/api/club/ClubApi";
import { useCaseChips } from "@/api/club/useClub";
import { routes } from "@/constants/routes";
import { getShadowStyle } from "@/theme/shadowStyle";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { CaseChipView } from "@/ui/components/club/CaseChip";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { SoftSection } from "@/ui/components/SoftSection";
import { StatusChip } from "@/ui/components/StatusChip";
import { formatTimestampDE } from "@/utils/isoDate";
import { useRouter } from "expo-router";
import React, { useMemo } from "react";
import { Text, View } from "react-native";

function itemTitle(item: ClubFeedItem): string {
  if (item.status === "withdrawn") return "Beitrag zurückgezogen";
  if (item.item_type === "event") return item.title ?? "Veranstaltung";
  if (item.item_type === "poll") return item.title ?? "Umfrage";
  return item.title?.trim() || (item.body?.slice(0, 80) ?? "Beitrag");
}

function itemSubtitle(item: ClubFeedItem): string {
  const parts = [
    item.author_display_name ?? "Mitglied",
    formatTimestampDE(item.created_at),
  ];
  if (item.item_type === "poll" && item.my_voted) parts.push("Du hast abgestimmt");
  if (item.item_type === "poll" && item.is_closed) parts.push("Geschlossen");
  if (item.comment_count) parts.push(`${item.comment_count} Kommentare`);
  return parts.join(" · ");
}

function FeedSnippet({ text }: { text: string | null | undefined }) {
  const { theme } = useTheme();
  const s = useStyles();
  const caseIds = useMemo(() => extractCaseIds(text), [text]);
  const { byId } = useCaseChips(caseIds, caseIds.length > 0);
  if (!text) return null;
  const segments = parseCaseTokens(text.slice(0, 200));
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
      {segments.map((seg, i) =>
        seg.kind === "text" ? (
          <Text key={i} style={[s.textTiny, { color: theme.color.muted }]}>
            {seg.value}
          </Text>
        ) : (
          <CaseChipView key={i} chip={byId[seg.caseId]} />
        )
      )}
    </View>
  );
}

function FeedSurface({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  const shadow = getShadowStyle(theme.cardShadow);
  return (
    <View
      style={{
        backgroundColor: theme.color.surface,
        borderRadius: theme.radius.md,
        borderWidth: 1,
        borderColor: theme.color.border,
        overflow: "hidden",
        ...shadow,
      }}
    >
      {children}
    </View>
  );
}

export function ClubFeed({
  items,
  onPressItem,
}: {
  items: ClubFeedItem[];
  onPressItem?: (item: ClubFeedItem) => void;
}) {
  const { theme } = useTheme();
  const router = useRouter();

  const go = (item: ClubFeedItem) => {
    if (onPressItem) onPressItem(item);
    else if (item.item_type === "event") {
      router.push(routes.myCalendarItem(item.item_id) as never);
    } else {
      router.push(routes.myClubItem(item.item_id) as never);
    }
  };

  const officialPinned = items.filter(
    (i) =>
      i.item_type === "post" &&
      i.is_official &&
      (i.pinned_at || i.is_official) &&
      i.status === "active"
  );
  const openPolls = items.filter(
    (i) =>
      i.item_type === "poll" &&
      i.status === "active" &&
      !i.is_closed &&
      !i.closed_at
  );

  // Dedup: show official once in hero, polls in section, then chronological posts
  const hero =
    officialPinned.find((i) => i.pinned_at) ??
    officialPinned[0] ??
    null;
  const heroId = hero?.item_id;
  const pollIds = new Set(openPolls.map((p) => p.item_id));
  const feedPosts = items.filter(
    (i) =>
      i.item_id !== heroId &&
      !pollIds.has(i.item_id) &&
      i.item_type !== "event"
  );

  const feedEmpty =
    !hero && openPolls.length === 0 && feedPosts.length === 0;

  if (feedEmpty) {
    return (
      <EmptyState
        title="Noch keine Beiträge."
        hint="Teile Neuigkeiten mit dem Verein – der erste Beitrag startet den Feed."
      />
    );
  }

  return (
    <View style={{ gap: theme.space.lg }}>
      {hero ? (
        <SoftSection title="Offizielle Mitteilung">
          <FeedSurface>
            <DenseRow
              title={itemTitle(hero)}
              meta={itemSubtitle(hero)}
              emphasized
              trailing={
                <StatusChip label="Offiziell" tone="primary" size="sm" />
              }
              onPress={() => go(hero)}
            >
              <FeedSnippet text={hero.body} />
            </DenseRow>
          </FeedSurface>
        </SoftSection>
      ) : null}

      {openPolls.length > 0 ? (
        <SoftSection title="Offene Umfragen">
          <FeedSurface>
            {openPolls.map((p) => (
              <DenseRow
                key={p.item_id}
                title={itemTitle(p)}
                meta={itemSubtitle(p)}
                trailing={
                  <StatusChip
                    label={p.my_voted ? "Abgestimmt" : "Umfrage"}
                    tone={p.my_voted ? "neutral" : "warning"}
                    size="sm"
                  />
                }
                onPress={() => go(p)}
              />
            ))}
          </FeedSurface>
        </SoftSection>
      ) : null}

      <SoftSection title="Beiträge">
        {feedPosts.length === 0 ? (
          <EmptyState
            title="Noch keine Beiträge."
            hint="Sobald jemand schreibt, erscheint es hier."
          />
        ) : (
          <FeedSurface>
            {feedPosts.map((item) => (
              <DenseRow
                key={item.item_id}
                title={itemTitle(item)}
                meta={itemSubtitle(item)}
                trailing={
                  item.item_type === "poll" ? (
                    <StatusChip label="Umfrage" tone="warning" size="sm" />
                  ) : item.is_official ? (
                    <StatusChip label="Offiziell" tone="primary" size="sm" />
                  ) : undefined
                }
                onPress={() => go(item)}
              >
                {item.item_type === "post" ? (
                  <FeedSnippet text={item.body} />
                ) : null}
              </DenseRow>
            ))}
          </FeedSurface>
        )}
      </SoftSection>
    </View>
  );
}
